/**
 * The editor's data model (M3): which fields exist, which are locked, and what
 * a stale-version conflict looks like laid side by side.
 *
 * The block tree has a fixed shape — no adding, removing or reordering (spec
 * §D15) — so the form is a flat list of descriptors derived from the body once,
 * rather than a nested editor. That keeps the RHF field names stable across a
 * locale switch, which is what makes the per-locale draft cache (AM-15.2) work
 * at all.
 */

import type {
  Block,
  BlogLocaleCopy,
  BlogLocaleEntry,
  BlogValidationIssue,
  FactDefinition,
  GlossaryEntry,
} from './types';

export type BlockFieldControl = 'input' | 'textarea';

export interface BlockFieldDescriptor {
  /** The path the API names in a validation issue, e.g. `body[7].text`. */
  path: string;
  label: string;
  control: BlockFieldControl;
  value: string;
  /** Translatable prose takes the locale's direction; ids and slugs do not. */
  content: boolean;
}

export interface BlockGroupDescriptor {
  index: number;
  kind: Block['kind'];
  heading: string;
  /**
   * Facts about this block that are equal across every locale by contract,
   * rendered read-only with that reason stated (V17).
   */
  invariants: { label: string; value: string }[];
  fields: BlockFieldDescriptor[];
}

/**
 * Why an invariant cannot be edited here.
 *
 * One sentence, used everywhere, because a different wording per field reads as
 * a different rule.
 */
export const INVARIANT_REASON = 'Equal across every locale by contract.';

function listLabel(index: number): string {
  return `Item ${String(index + 1)}`;
}

/** Flatten one block into its editable fields and its locked facts. */
export function describeBlock(block: Block, index: number): BlockGroupDescriptor {
  const base = `body[${String(index)}]`;

  switch (block.kind) {
    case 'heading':
      return {
        index,
        kind: 'heading',
        heading: `Heading (level ${String(block.level)})`,
        invariants: [
          { label: 'Anchor id', value: block.id },
          { label: 'Level', value: `h${String(block.level)}` },
        ],
        fields: [
          { path: `${base}.text`, label: 'Heading text', control: 'input', value: block.text, content: true },
        ],
      };
    case 'paragraph':
      return {
        index,
        kind: 'paragraph',
        heading: 'Paragraph',
        invariants: [],
        fields: [
          { path: `${base}.text`, label: 'Text', control: 'textarea', value: block.text, content: true },
        ],
      };
    case 'list':
      return {
        index,
        kind: 'list',
        heading: block.ordered ? 'Ordered list' : 'Bulleted list',
        invariants: [{ label: 'Ordered', value: block.ordered ? 'yes' : 'no' }],
        fields: block.items.map((item, itemIndex) => ({
          path: `${base}.items[${String(itemIndex)}]`,
          label: listLabel(itemIndex),
          control: 'input' as const,
          value: item,
          content: true,
        })),
      };
    case 'callout':
      return {
        index,
        kind: 'callout',
        heading: `Callout (${block.tone})`,
        invariants: [{ label: 'Tone', value: block.tone }],
        fields: [
          { path: `${base}.title`, label: 'Callout title', control: 'input', value: block.title, content: true },
          { path: `${base}.text`, label: 'Callout text', control: 'textarea', value: block.text, content: true },
        ],
      };
    case 'figure':
      return {
        index,
        kind: 'figure',
        heading: 'Figure',
        invariants: [{ label: 'Screenshot', value: block.screenshot }],
        fields: [
          { path: `${base}.alt`, label: 'Alt text', control: 'input', value: block.alt, content: true },
          { path: `${base}.caption`, label: 'Caption', control: 'input', value: block.caption, content: true },
        ],
      };
    case 'step':
      return {
        index,
        kind: 'step',
        heading: 'Step',
        invariants: [
          { label: 'Hands-on minutes', value: `${String(block.minutes)} min` },
          ...(block.figure === undefined
            ? []
            : [{ label: 'Screenshot', value: block.figure.screenshot }]),
        ],
        fields: [
          { path: `${base}.title`, label: 'Step title', control: 'input', value: block.title, content: true },
          { path: `${base}.text`, label: 'Step text', control: 'textarea', value: block.text, content: true },
          ...(block.figure === undefined
            ? []
            : [
                {
                  path: `${base}.figure.alt`,
                  label: 'Step figure alt text',
                  control: 'input' as const,
                  value: block.figure.alt,
                  content: true,
                },
                {
                  path: `${base}.figure.caption`,
                  label: 'Step figure caption',
                  control: 'input' as const,
                  value: block.figure.caption,
                  content: true,
                },
              ]),
        ],
      };
    case 'faq':
      return {
        index,
        kind: 'faq',
        heading: 'Questions',
        invariants: [],
        fields: block.items.flatMap((item, itemIndex) => [
          {
            path: `${base}.items[${String(itemIndex)}].question`,
            label: `Question ${String(itemIndex + 1)}`,
            control: 'input' as const,
            value: item.question,
            content: true,
          },
          {
            path: `${base}.items[${String(itemIndex)}].answer`,
            label: `Answer ${String(itemIndex + 1)}`,
            control: 'textarea' as const,
            value: item.answer,
            content: true,
          },
        ]),
      };
    default:
      return {
        index,
        kind: 'keyTakeaways',
        heading: 'Key takeaways',
        invariants: [],
        fields: [
          { path: `${base}.title`, label: 'Takeaways title', control: 'input', value: block.title, content: true },
          ...block.items.map((item, itemIndex) => ({
            path: `${base}.items[${String(itemIndex)}]`,
            label: listLabel(itemIndex),
            control: 'input' as const,
            value: item,
            content: true,
          })),
        ],
      };
  }
}

export function describeBody(body: readonly Block[]): BlockGroupDescriptor[] {
  return body.map((block, index) => describeBlock(block, index));
}

/**
 * Whether this locale's address may still change (Q13, reconciliation #9).
 *
 * The lock is per locale COPY, not per cluster: a fresh Finnish copy inside an
 * already-published post has never been an address anyone holds, so nothing
 * breaks by renaming it. Once *that* copy has published once, the address is a
 * promise and stops being editable.
 */
export function describePathSlugLock(entry: BlogLocaleEntry | undefined): {
  locked: boolean;
  reason: string;
} {
  const published = entry?.publishedVersion;
  if (published === null || published === undefined) {
    return {
      locked: false,
      reason: 'This copy has never published, so its address can still change.',
    };
  }
  return {
    locked: true,
    reason: `This copy published at v${String(published)}. Its address is the promise search results and inbound links already point at, so it can no longer change.`,
  };
}

// ============================================================================
// Fact values (AM-18)
// ============================================================================

/** The token an author places in prose so the number is never retyped. */
export function factToken(id: string): string {
  return `%%fact:${id}%%`;
}

/**
 * One fact, as the per-locale editor meets it.
 *
 * `id`, `token` and `source` are context and are rendered read-only: what a
 * fact means and where it was verified do not vary by language, and a
 * translator who could edit them could make two locales disagree about where a
 * number came from. `value` is the only writable half, because that is the only
 * half that is language.
 */
export interface FactValueField {
  /** The react-hook-form name, `v0`, `v1`, … See `flattenFields` for why. */
  key: string;
  id: string;
  token: string;
  /** `file:line`, or the observation that established the number. */
  source: string;
  /** This locale's rendering. `''` means nobody has written it yet. */
  value: string;
  /** Whether the store has a rendering at all, as opposed to a blank one. */
  written: boolean;
}

/**
 * A control per fact the CLUSTER defines, not per value the copy happens to
 * hold.
 *
 * Driving this off the definitions is the whole point: a fact the German copy
 * has no entry for still gets a row, empty, so the gap is something a
 * translator sees and fills rather than something only the publish gate knows
 * about.
 */
export function flattenFactValues(
  facts: readonly FactDefinition[],
  factValues: Readonly<Record<string, string>>,
): FactValueField[] {
  return facts.map((fact, index) => {
    const stored = factValues[fact.id];
    return {
      key: `v${String(index)}`,
      id: fact.id,
      token: factToken(fact.id),
      source: fact.source,
      value: stored ?? '',
      written: stored !== undefined,
    };
  });
}

/**
 * The submitted map, keyed back by fact id rather than by control name.
 *
 * Mirrors `applyFieldValues`: a field the form never registered keeps the value
 * it was seeded with, so a partially-submitted form cannot blank a rendering
 * that was already stored.
 */
export function applyFactValues(
  fields: readonly FactValueField[],
  values: Record<string, string>,
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const field of fields) {
    resolved[field.id] = values[field.key] ?? field.value;
  }
  return resolved;
}

// ============================================================================
// Reading the two new validator codes (AM-18)
// ============================================================================

/** A locale references a fact the cluster defines, but has no wording for it. */
export const FACT_VALUE_MISSING = 'fact_value_missing';
/** A copy references a fact id the cluster does not define at all. */
export const FACT_DEFINITION_DRIFT = 'fact_definition_drift';

/**
 * Why a missing value is a job rather than a fault.
 *
 * Stated once, in these words, because `fact_value_missing` is the code an
 * editor will meet most often and the wrong framing makes it unactionable: it
 * is not corruption, it is a sentence nobody has written yet, and the person
 * who writes it must write it in their own language.
 */
export const FACT_GAP_GUIDANCE =
  'Nothing is broken here — these are unwritten. Each of these copies uses a fact but has no wording for it yet. Open the copy and write the value the way that language writes it: 14 Tage, not 14 days.';

/** Why a drifted id is a fault, and one that a translator must not fix. */
export const FACT_DRIFT_GUIDANCE =
  'These copies reference a fact id this post does not define. Either a definition was renamed on the shared panel or a body carries a token nobody declared — fix it on the shared panel, never by editing the translation.';

/**
 * The locale a validator path names, when it names one.
 *
 * Cluster-level issues are addressed `locales.{code}.{rest}` by the validator,
 * which is what lets the panel offer to open the copy the issue is about rather
 * than leaving the reader to work out which language it means.
 */
export function localeFromIssuePath(path: string): string | null {
  return /^locales\.([^.]+)(?:\.|$)/.exec(path)?.[1] ?? null;
}

export interface LocaleIssue {
  issue: BlogValidationIssue;
  /** Null when the path is not cluster-addressed; no copy to jump to. */
  locale: string | null;
}

/**
 * The issues that belong to one copy, re-addressed as that copy's own fields.
 *
 * The cached verdict is a statement about every copy at once, so it
 * addresses everything `locales.{code}.{path}`. A form editing one of them
 * needs `factValues.trial-days`, not `locales.de.factValues.trial-days` — and
 * needs every other language's issues kept off it entirely, or an editor
 * writing Finnish is shown German's problems on their own controls.
 *
 * An issue naming a copy and nothing further (`locales.fi`, a whole copy that
 * does not exist) keeps its path: it is a statement about the copy, not about a
 * field on it, and there is no control for it to land on.
 */
export function issuesForLocale(
  issues: readonly BlogValidationIssue[],
  locale: string,
): BlogValidationIssue[] {
  const mine: BlogValidationIssue[] = [];
  for (const issue of issues) {
    const at = localeFromIssuePath(issue.path);
    // Already relative — a single-copy verdict never carries the prefix.
    if (at === null) {
      mine.push(issue);
      continue;
    }
    if (at !== locale) continue;
    const prefix = `locales.${at}.`;
    mine.push(
      issue.path.startsWith(prefix)
        ? { ...issue, path: issue.path.slice(prefix.length) }
        : issue,
    );
  }
  return mine;
}

export interface PartitionedIssues {
  /** `fact_value_missing` — actionable gaps, kept apart from the failures. */
  gaps: LocaleIssue[];
  /** `fact_definition_drift` — a real inconsistency between cluster and copy. */
  drifts: LocaleIssue[];
  /** Everything else, rendered exactly as it always was. */
  rest: BlogValidationIssue[];
}

/**
 * Sort a verdict's errors into the two fact codes and everything else.
 *
 * They are separated because they read differently: a missing rendering names a
 * person and a task, a drifted id names a defect. Listing both in one
 * undifferentiated column is how a multi-language post's validation panel
 * becomes something nobody reads.
 */
export function partitionFactIssues(
  issues: readonly BlogValidationIssue[],
): PartitionedIssues {
  const gaps: LocaleIssue[] = [];
  const drifts: LocaleIssue[] = [];
  const rest: BlogValidationIssue[] = [];
  for (const issue of issues) {
    if (issue.code === FACT_VALUE_MISSING) {
      gaps.push({ issue, locale: localeFromIssuePath(issue.path) });
    } else if (issue.code === FACT_DEFINITION_DRIFT) {
      drifts.push({ issue, locale: localeFromIssuePath(issue.path) });
    } else {
      rest.push(issue);
    }
  }
  return { gaps, drifts, rest };
}

export interface ConflictFieldPair {
  path: string;
  label: string;
  mine: string;
  theirs: string;
  changed: boolean;
}

/**
 * Lay my unsaved values beside the head that beat me to it (D17/M3-S6).
 *
 * Both sides are always carried. Neither "take theirs" nor "keep mine" may
 * discard the other's text before the editor has chosen, which is the whole
 * contract the spec states: the editor chooses, the client never re-merges.
 */
export function pairConflictFields(args: {
  mine: {
    title: string;
    description: string;
    excerpt: string;
    pathSlug: string | null;
    keywords: readonly string[];
    body: readonly Block[];
    glossary: readonly GlossaryEntry[];
    factValues: Readonly<Record<string, string>>;
  };
  theirs: BlogLocaleCopy;
}): ConflictFieldPair[] {
  const { mine, theirs } = args;
  const pairs: ConflictFieldPair[] = [
    { path: 'title', label: 'Title', mine: mine.title, theirs: theirs.title },
    { path: 'description', label: 'Description', mine: mine.description, theirs: theirs.description },
    { path: 'excerpt', label: 'Excerpt', mine: mine.excerpt, theirs: theirs.excerpt },
    {
      path: 'pathSlug',
      label: 'URL path',
      mine: mine.pathSlug ?? '',
      theirs: theirs.pathSlug ?? '',
    },
    {
      path: 'keywords',
      label: 'Keywords',
      mine: joinKeywords(mine.keywords),
      theirs: joinKeywords(theirs.keywords),
    },
    ...describeBody(mine.body).flatMap((group) =>
      group.fields.map((field) => ({
        path: field.path,
        label: `${group.heading} — ${field.label}`,
        mine: field.value,
        theirs: valueAtPath(describeBody(theirs.body), field.path),
      })),
    ),
    ...mine.glossary.map((entry, index) => ({
      path: `glossary[${String(index)}].definition`,
      label: `Glossary — ${entry.term}`,
      mine: entry.definition,
      theirs: theirs.glossary[index]?.definition ?? '',
    })),
    // Fact renderings are per locale now, so two editors can genuinely collide
    // on one. Carrying them makes that collision visible; leaving them out
    // would let "keep mine" quietly decide a number's wording.
    ...sortedFactValues(mine.factValues).map(([id, value]) => ({
      path: `factValues.${id}`,
      label: `Fact value — ${id}`,
      mine: value,
      theirs: theirs.factValues[id] ?? '',
    })),
  ].map((pair) => ({ ...pair, changed: pair.mine !== pair.theirs }));

  return pairs;
}

/** The form's own representation: one keyword per line, in stored order. */
function joinKeywords(keywords: readonly string[]): string {
  return keywords.join('\n');
}

/**
 * Fact renderings in id order.
 *
 * Sorted rather than left in insertion order because the map arrives as JSON
 * from two different reads — a head and a revision — and a diff whose rows
 * reorder between them reads as a change that did not happen.
 */
function sortedFactValues(
  factValues: Readonly<Record<string, string>>,
): [string, string][] {
  return Object.entries(factValues).sort(([left], [right]) => left.localeCompare(right));
}

function valueAtPath(groups: readonly BlockGroupDescriptor[], path: string): string {
  for (const group of groups) {
    for (const field of group.fields) {
      if (field.path === path) return field.value;
    }
  }
  return '';
}

/** Only the fields that actually differ — the dialog shows nothing else. */
export function conflictingFields(pairs: readonly ConflictFieldPair[]): ConflictFieldPair[] {
  return pairs.filter((pair) => pair.changed);
}

/**
 * The blast-radius sentence on the cluster panel's confirm (Q14).
 *
 * It names the number of locales from the payload rather than the word
 * for the count, so it stays true the day a language is added or retired.
 */
export function clusterBlastRadiusCopy(localeCount: number): string {
  return `This changes all ${String(localeCount)} locales at once. Every copy renders the new value the next time this post is published.`;
}

/** The publish confirm names the deploy, not just the save (D16). */
export function publishConfirmCopy(localeCount: number): string {
  return `Publishing rebuilds and deploys ${String(localeCount)} locales. Readers see the new copy as soon as the build lands.`;
}

// ============================================================================
// Form ↔ body marshalling
// ============================================================================

export interface FlatField {
  /** The react-hook-form name, `f0`, `f1`, … */
  key: string;
  /** The API path, `body[7].text`. */
  path: string;
  label: string;
  control: BlockFieldControl;
  value: string;
  groupIndex: number;
}

/**
 * Flatten the descriptors into the shape the form registers.
 *
 * RHF reads `[` and `.` in a field name as structure, so `body[7].text` would
 * register a nested array of one string rather than one string. The synthetic
 * key sidesteps that, and this list is how a synthetic key finds its way back
 * to the path the API names in a validation issue.
 */
export function flattenFields(groups: readonly BlockGroupDescriptor[]): FlatField[] {
  const flat: FlatField[] = [];
  for (const group of groups) {
    for (const field of group.fields) {
      flat.push({
        key: `f${String(flat.length)}`,
        path: field.path,
        label: field.label,
        control: field.control,
        value: field.value,
        groupIndex: group.index,
      });
    }
  }
  return flat;
}

/**
 * Every comparable string in a copy, flat, for the diff.
 *
 * Metadata first, then the body in reading order — the order a reviewer scans,
 * not the order the JSON happens to be in.
 */
export function comparableFields(copy: {
  title: string;
  description: string;
  excerpt: string;
  pathSlug: string | null;
  keywords: readonly string[];
  body: readonly Block[];
  glossary: readonly GlossaryEntry[];
  factValues: Readonly<Record<string, string>>;
}): { path: string; label: string; value: string }[] {
  return [
    { path: 'title', label: 'Title', value: copy.title },
    { path: 'description', label: 'Description', value: copy.description },
    { path: 'excerpt', label: 'Excerpt', value: copy.excerpt },
    { path: 'pathSlug', label: 'URL path', value: copy.pathSlug ?? '' },
    { path: 'keywords', label: 'Keywords', value: joinKeywords(copy.keywords) },
    ...describeBody(copy.body).flatMap((group) =>
      group.fields.map((field) => ({
        path: field.path,
        label: `${group.heading} — ${field.label}`,
        value: field.value,
      })),
    ),
    ...copy.glossary.map((entry, index) => ({
      path: `glossary[${String(index)}].definition`,
      label: `Glossary — ${entry.term}`,
      value: entry.definition,
    })),
    ...sortedFactValues(copy.factValues).map(([id, value]) => ({
      path: `factValues.${id}`,
      label: `Fact value — ${id}`,
      value,
    })),
  ];
}

export function defaultFieldValues(flat: readonly FlatField[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of flat) values[field.key] = field.value;
  return values;
}

/** The metadata paths whose API name and control name are the same string. */
const METADATA_PATHS: readonly string[] = [
  'title',
  'description',
  'excerpt',
  'pathSlug',
  'keywords',
];

/**
 * Path → RHF name, for mapping a server validation issue onto a control.
 *
 * Takes the fact fields as well as the block fields because both use synthetic
 * control names: a `fact_value_missing` on `factValues.trial-days` has a
 * control on this form, and an issue with a control must never fall through to
 * the orphan list.
 */
export function fieldNameForPath(
  flat: readonly FlatField[],
  factFields: readonly FactValueField[],
  path: string,
): string | undefined {
  const direct = flat.find((field) => field.path === path);
  if (direct !== undefined) return `fields.${direct.key}`;
  const fact = factFields.find((field) => `factValues.${field.id}` === path);
  if (fact !== undefined) return `factValues.${fact.key}`;
  if (METADATA_PATHS.includes(path)) return path;
  return undefined;
}

function readValue(values: Record<string, string>, flat: readonly FlatField[], path: string): string {
  const field = flat.find((candidate) => candidate.path === path);
  if (field === undefined) return '';
  return values[field.key] ?? field.value;
}

/**
 * Write the edited strings back into the block tree.
 *
 * The tree's *shape* is never touched — no block is added, removed or moved —
 * so this rebuilds each block from its original with only its text fields
 * replaced. Anything the form does not own (ids, levels, tones, screenshot
 * bindings, `minutes`) is carried through untouched, which is what makes the
 * read-only invariants actually invariant rather than merely un-rendered.
 */
export function applyFieldValues(
  body: readonly Block[],
  flat: readonly FlatField[],
  values: Record<string, string>,
): Block[] {
  return body.map((block, index) => {
    const base = `body[${String(index)}]`;
    const at = (suffix: string): string => readValue(values, flat, `${base}${suffix}`);

    switch (block.kind) {
      case 'heading':
        return { ...block, text: at('.text') };
      case 'paragraph':
        return { ...block, text: at('.text') };
      case 'list':
        return { ...block, items: block.items.map((_, i) => at(`.items[${String(i)}]`)) };
      case 'callout':
        return { ...block, title: at('.title'), text: at('.text') };
      case 'figure':
        return { ...block, alt: at('.alt'), caption: at('.caption') };
      case 'step':
        return {
          ...block,
          title: at('.title'),
          text: at('.text'),
          ...(block.figure === undefined
            ? {}
            : {
                figure: {
                  ...block.figure,
                  alt: at('.figure.alt'),
                  caption: at('.figure.caption'),
                },
              }),
        };
      case 'faq':
        return {
          ...block,
          items: block.items.map((item, i) => ({
            ...item,
            question: at(`.items[${String(i)}].question`),
            answer: at(`.items[${String(i)}].answer`),
          })),
        };
      default:
        return {
          ...block,
          title: at('.title'),
          items: block.items.map((_, i) => at(`.items[${String(i)}]`)),
        };
    }
  });
}
